// https://stackoverflow.com/questions/54242039/intl-numberformat-space-character-does-not-match
export const transformNonBreakingSpacesToSpace = (str: string) => {
  return str.replace(/\u202f/g, " ").replace(/\xa0/g, " ");
};

type FormatPriceOptions = {
  nonBreakingSpace?: boolean;
  fractionDigits?: number;
  asCents?: boolean;
};

export const formatPrice = (
  price: number | string,
  fractionDigits = 0, // @TODO refactor these arguments
  nonBreakingSpace = true, // @TODO refactor these arguments
  options: FormatPriceOptions = {},
) => {
  const {
    fractionDigits: fractionDigitsOption,
    nonBreakingSpace: nonBreakingSpaceOption,
    asCents = false,
  } = options;

  let finalePrice = typeof price === "string" ? parseFloat(price) : price;
  if (Number.isNaN(finalePrice) || finalePrice === null || finalePrice === undefined) {
    finalePrice = 0;
  }
  if (asCents) finalePrice = centsToPrice(finalePrice);

  const formattedPrice = finalePrice.toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: fractionDigitsOption ?? fractionDigits,
  });
  if (nonBreakingSpaceOption ?? nonBreakingSpace) return formattedPrice;
  return transformNonBreakingSpacesToSpace(formattedPrice);
};

export const centsToPrice = (cents: number) => cents / 100;

export const priceToCents = (price: number) => Math.round(price * 100);

export const centsToPriceFormated = (cents: number) => (cents / 100).toFixed(2);

export const formatPercent = (percent: number | string) => {
  const finalePercent = (typeof percent === "string" ? parseFloat(percent) : percent) / 100;
  return finalePercent.toLocaleString("fr-FR", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

export const convertToDecimalFraction = (percent: number) => percent / 100;

export const convertDecimalFractionToInt = (percent: number) => percent * 100;

export const getIncludedTaxesPrice = (exclTaxesPrice: number, vatRate: number) =>
  exclTaxesPrice * (1 + vatRate / 10000);

export const getExcludedTaxesPrice = (price: number, vatRate: number) => {
  const denominator = 1 + vatRate / 10000;
  if (denominator <= 0) {
    throw new Error(
      `Invalid VAT rate: ${vatRate}. VAT rate must be greater than -10000.`
    );
  }
  return vatRate !== 0 ? price / denominator : price;
};

export const getVAT = (price: number, vatRate: number) =>
  price - getExcludedTaxesPrice(price, vatRate);

export const REFUND_FEE_HT_PER_ITEM = 100;
export const REFUND_FEE_VAT_RATE = 2000;

export const computeRefundFeeTtc = (refundedItemsCount: number): number => {
  const feeHtTotal = refundedItemsCount * REFUND_FEE_HT_PER_ITEM;
  return Math.round(getIncludedTaxesPrice(feeHtTotal, REFUND_FEE_VAT_RATE));
};

export const parseAmountToNumber = (
  value: string | number | null | undefined
): number => {
  if (value === undefined || value === null) return 0;
  if (typeof value === "number") return value;
  const normalized = value
    .toString()
    .replace(/\s/g, "")
    .replace("€", "")
    .replace(",", ".")
    .replace(/[^\d.-]/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

export type RefundCreditNoteItemLike = {
  orderItem?: {
    finalTotal?: number | null;
    vatRate?: number | null;
  } | null;
};

export const computeRefundBaseTotalsFromCreditNoteItems = (
  creditNoteItems: RefundCreditNoteItemLike[],
): { baseHt: number; baseTtc: number } | null => {
  if (!creditNoteItems || creditNoteItems.length === 0) return null;

  const totalsByVatRate = creditNoteItems.reduce((acc, creditNoteItem) => {
    const finalTotal = creditNoteItem.orderItem?.finalTotal ?? null;
    const itemVatRate = creditNoteItem.orderItem?.vatRate ?? null;
    if (finalTotal === null || itemVatRate === null) return acc;

    acc.set(itemVatRate, (acc.get(itemVatRate) ?? 0) + finalTotal);
    return acc;
  }, new Map<number, number>());

  const refundedItemsCount = creditNoteItems.filter(
    (creditNoteItem) => !!creditNoteItem.orderItem,
  ).length;

  const refundedItemsTotalTtc = Array.from(totalsByVatRate.values()).reduce(
    (sum, value) => sum + value,
    0,
  );

  const refundedItemsTotalHt = Array.from(totalsByVatRate.entries()).reduce(
    (sum, [vatRate, totalTtc]) => sum + getExcludedTaxesPrice(totalTtc, vatRate),
    0,
  );

  const feeHtTotal = refundedItemsCount * REFUND_FEE_HT_PER_ITEM;
  const feeTtcTotal = computeRefundFeeTtc(refundedItemsCount);

  const baseTtc = refundedItemsTotalTtc + feeTtcTotal;
  const baseHt = refundedItemsTotalHt + feeHtTotal;

  if (baseTtc <= 0 || baseHt <= 0) return null;

  return { baseHt, baseTtc };
};
